import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Toolbar,
  Typography,
  Card,
  CardContent,
  Grid,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  CheckCircle as ActivateIcon,
  Cancel as DeactivateIcon,
  AccountTree as WorkflowIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchWorkflows,
  fetchRoles,
  fetchWorkflowStatistics,
  deleteWorkflow,
  setSearchTerm,
  setFilters,
  setPagination,
  setSelectedWorkflow,
  setDialogOpen,
  setEditing,
  setSelectedWorkflowIds,
  toggleWorkflowSelection,
  selectAllWorkflows,
  deselectAllWorkflows,
  bulkActivateWorkflows,
  bulkDeactivateWorkflows,
} from '../../store/reducers/workflowSlice';
import { Workflow } from '../../services/workflowApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  searchBox: {
    marginBottom: theme.spacing(2),
  },
  filtersBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  spacer: {
    flex: '1 1 100%',
  },
  actions: {
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  rolesCell: {
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const WorkflowList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    workflows,
    roles,
    loading,
    error,
    searchTerm,
    filters,
    pagination,
    statistics,
    selectedWorkflowIds,
    bulkLoading,
  } = useSelector((state: RootState) => state.workflow);

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchWorkflows());
    dispatch(fetchRoles());
    dispatch(fetchWorkflowStatistics());
  }, [dispatch]);

  // Filter and search workflows
  const filteredWorkflows = useMemo(() => {
    let filtered = workflows;

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(workflow =>
        workflow.ProcessName.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    if (filters.active !== 'all') {
      filtered = filtered.filter(workflow => 
        workflow.Active === (filters.active === 'true')
      );
    }

    if (filters.roleId) {
      filtered = filtered.filter(workflow => 
        workflow.Roles && workflow.Roles.some(role => role.Id === filters.roleId)
      );
    }

    return filtered;
  }, [workflows, searchTerm, filters]);

  // Pagination
  const paginatedWorkflows = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    return filteredWorkflows.slice(startIndex, startIndex + pagination.pageSize);
  }, [filteredWorkflows, pagination.page, pagination.pageSize]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleFilterChange = (filterName: string, value: any) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPagination({ page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPagination({ 
      pageSize: parseInt(event.target.value, 10),
      page: 1 
    }));
  };

  const handleAddWorkflow = () => {
    dispatch(setSelectedWorkflow(null));
    dispatch(setEditing(false));
    dispatch(setDialogOpen(true));
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    dispatch(setSelectedWorkflow(workflow));
    dispatch(setEditing(true));
    dispatch(setDialogOpen(true));
  };

  const handleDeleteWorkflow = (id: number) => {
    if (window.confirm('Are you sure you want to delete this workflow?')) {
      dispatch(deleteWorkflow(id));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(selectAllWorkflows());
    } else {
      dispatch(deselectAllWorkflows());
    }
  };

  const handleSelectWorkflow = (workflowId: number) => {
    dispatch(toggleWorkflowSelection(workflowId));
  };

  const handleBulkActivate = () => {
    if (selectedWorkflowIds.length > 0) {
      dispatch(bulkActivateWorkflows(selectedWorkflowIds));
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedWorkflowIds.length > 0) {
      dispatch(bulkDeactivateWorkflows(selectedWorkflowIds));
    }
  };

  const getRoleById = (roleId: number) => {
    return roles.find(role => role.Id === roleId);
  };

  const formatRoles = (workflow: Workflow): string => {
    if (workflow.Roles && workflow.Roles.length > 0) {
      return workflow.Roles
        .map(role => role.Name || `Role ${role.Id}`)
        .join(', ');
    }
    return 'No roles assigned';
  };

  const getApprovalLevelsCount = (workflow: Workflow): number => {
    return workflow.ApprovalLevels ? workflow.ApprovalLevels.length : 0;
  };

  const isSelected = (workflowId: number) => selectedWorkflowIds.includes(workflowId);
  const numSelected = selectedWorkflowIds.length;

  if (loading && workflows.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue}>
                {statistics.total}
              </Typography>
              <Typography className={classes.statLabel}>
                Total Workflows
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue} style={{ color: '#4caf50' }}>
                {statistics.active}
              </Typography>
              <Typography className={classes.statLabel}>
                Active Workflows
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue} style={{ color: '#f44336' }}>
                {statistics.inactive}
              </Typography>
              <Typography className={classes.statLabel}>
                Inactive Workflows
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box className={classes.searchBox}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search workflows by process name..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon color="action" />,
          }}
        />
      </Box>

      {/* Filters */}
      <Box className={classes.filtersBox}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="subtitle2">
              <FilterIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Filters:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.active}
                onChange={(e) => handleFilterChange('active', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={filters.roleId || ''}
                onChange={(e) => handleFilterChange('roleId', e.target.value || null)}
                label="Role"
              >
                <MenuItem value="">All Roles</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.Id} value={role.Id}>
                    {role.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Toolbar */}
      <Toolbar className={classes.toolbar}>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography variant="h6">
            Workflows ({filteredWorkflows.length})
          </Typography>
        )}
        <div className={classes.spacer} />
        <div className={classes.actions}>
          {numSelected > 0 ? (
            <>
              <Tooltip title="Activate Selected">
                <IconButton 
                  onClick={handleBulkActivate}
                  disabled={bulkLoading}
                >
                  <ActivateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Deactivate Selected">
                <IconButton 
                  onClick={handleBulkDeactivate}
                  disabled={bulkLoading}
                >
                  <DeactivateIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Export">
                <IconButton>
                  <ExportIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddWorkflow}
              >
                Add New Workflow
              </Button>
            </>
          )}
        </div>
      </Toolbar>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < filteredWorkflows.length}
                  checked={filteredWorkflows.length > 0 && numSelected === filteredWorkflows.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Process Name</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Approval Levels</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedWorkflows.map((workflow) => {
              const isItemSelected = isSelected(workflow.Id);
              return (
                <TableRow
                  key={workflow.Id}
                  hover
                  selected={isItemSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isItemSelected}
                      onChange={() => handleSelectWorkflow(workflow.Id)}
                    />
                  </TableCell>
                  <TableCell>{workflow.ProcessName}</TableCell>
                  <TableCell className={classes.rolesCell}>
                    <Tooltip title={formatRoles(workflow)}>
                      <span>{formatRoles(workflow)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${getApprovalLevelsCount(workflow)} levels`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={workflow.Active ? 'Active' : 'Inactive'}
                      className={workflow.Active ? classes.activeChip : classes.inactiveChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small"
                        onClick={() => handleEditWorkflow(workflow)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteWorkflow(workflow.Id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredWorkflows.length}
        rowsPerPage={pagination.pageSize}
        page={pagination.page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
};

export default WorkflowList; 